/* eslint-disable max-lines */
/* Landing page messages, merged under the `landing` key of every locale (see ./index.js). */

const en = {
  langLabel: 'Language',
  nav: { who: 'Who it\'s for', features: 'Features', how: 'How it works', start: 'Get started' },
  pill: 'Open source · Optional data sharing with Connect',
  title: 'Your child\'s voice, in pictures',
  subtitle: 'Picture boards that let your child point, tap, and be heard — right in your browser.',
  heroRoles: 'Parents set up the boards in the panel. Children use them in the app.',
  forParents: 'For parents',
  openPanel: 'Open the panel',
  forChildren: 'For children',
  openApp: 'Open the app',
  seeHow: 'See how it works ↓',
  heroAlt: 'A mavoix board with picture cards',
  aboutPre: 'mavoix is an open source application enabling non-verbal persons to communicate using ',
  aboutLink: 'images (PECS)',
  aboutPost: '. Build boards for home, school, or therapy — and let taps speak aloud.',
  sharingTitle: 'Sharing is caring',
  sharingPre: 'mavoix can optionally share usage data with',
  sharingPost: '— an open data platform for assistive applications. Sharing is your choice: turn it on or off at any time in Global settings. What is shared: session times and the sentences built.',
  dataTitle: 'Statistics and insight',
  dataBody: 'The usage statistics page shows how the app is used: sessions, days of use and the sentences built, for your account only. If you choose to share with Connect, researchers and builders can also see how picture-based communication is used in practice, so assistive tools grow from evidence, not guesswork.',
  who: {
    title: 'Who it\'s for',
    parentsTitle: 'Parents',
    parentsBody: 'Give your child a way to tell you what they want, how they feel, and what they need — even before words come.',
    caregiversTitle: 'Caregivers',
    caregiversBody: 'Set up boards tailored to daily routines and familiar things, ready whenever you need them.',
    educatorsTitle: 'Educators',
    educatorsBody: 'A flexible tool that supports communication goals in the classroom or therapy room, at whatever pace works best.'
  },
  features: {
    title: 'What you can do',
    boardsTitle: 'Build boards for any moment',
    boardsBody: 'Create a board for home, school, feelings, or any routine. Boards stay organised as your child\'s needs grow.',
    picturesTitle: 'Add pictures',
    picturesBody: 'Upload photos from your camera roll, search a symbol library, or snap something new right in the app.',
    speaksTitle: 'Speaks for them',
    speaksBody: 'Your child drops picture cards into a sentence and the app says it aloud — giving them an instant, clear voice.',
    organisedTitle: 'Stays organised as they grow',
    organisedBody: 'Rename, reorder, and reorganise boards at any time — search, filters, and categories keep things clear.'
  },
  how: {
    title: 'How it works',
    step1Title: 'Make a board',
    step1Body: 'Name it anything — Morning Routine, School, Feelings. Takes about ten seconds.',
    step2Title: 'Add pictures',
    step2Body: 'Upload photos from your camera roll, search a symbol library, or snap something new right in the app.',
    step3Title: 'Link the device',
    step3Body: 'Scan a QR code from the panel with your child\'s tablet or phone. Their boards appear there.',
    step4Title: 'Let them speak',
    step4Body: 'Your child drops pictures into the sentence strip and plays it: the app says the sentence out loud. Simple, immediate, and theirs.',
    deviceLink: 'Open on your child\'s device'
  },
  oss: {
    title: 'Open source, always',
    lead: 'mavoix is open source. We believe every child deserves a voice — no subscription, no barriers, no hidden costs.',
    contribute: 'Want to help? Code, translations, and feedback are always welcome.',
    github: 'View on GitHub',
    app: 'mavoix app'
  }
}

const fr = {
  langLabel: 'Langue',
  nav: { who: 'Pour qui ?', features: 'Fonctionnalités', how: 'Comment ça marche', start: 'Commencer' },
  pill: 'Open source · Partage de données facultatif avec Connect',
  title: 'La voix de votre enfant, en images',
  subtitle: 'Des tableaux d\'images qui permettent à votre enfant de montrer, toucher et se faire entendre — directement dans votre navigateur.',
  heroRoles: 'Les parents préparent les tableaux dans le panneau. Les enfants les utilisent dans l\'application.',
  forParents: 'Pour les parents',
  openPanel: 'Ouvrir le panneau',
  forChildren: 'Pour les enfants',
  openApp: 'Ouvrir l\'application',
  seeHow: 'Voir comment ça marche ↓',
  heroAlt: 'Un tableau mavoix avec des cartes images',
  aboutPre: 'mavoix est une application open source qui permet aux personnes non verbales de communiquer à l\'aide d\'',
  aboutLink: 'images (PECS)',
  aboutPost: '. Créez des tableaux pour la maison, l\'école ou la thérapie — et laissez les pressions parler à voix haute.',
  sharingTitle: 'Partager, c\'est prendre soin',
  sharingPre: 'mavoix peut partager des données d\'usage avec',
  sharingPost: '— une plateforme de données ouvertes pour les applications d\'assistance. Le partage est votre choix : activez-le ou désactivez-le à tout moment dans Paramètres globaux. Sont partagés : les durées d\'utilisation et les phrases construites.',
  dataTitle: 'Des statistiques pour comprendre',
  dataBody: 'La page de statistiques d\'utilisation montre comment l\'application est utilisée : sessions, jours d\'utilisation et phrases construites, pour votre compte uniquement. Si vous choisissez de partager avec Connect, chercheurs et concepteurs peuvent aussi voir comment la communication par images est utilisée en pratique, pour que les outils d\'assistance évoluent à partir de preuves et non de suppositions.',
  who: {
    title: 'Pour qui ?',
    parentsTitle: 'Parents',
    parentsBody: 'Donnez à votre enfant un moyen de vous dire ce qu\'il veut, ce qu\'il ressent et ce dont il a besoin — avant même l\'arrivée des mots.',
    caregiversTitle: 'Accompagnants',
    caregiversBody: 'Préparez des tableaux adaptés aux routines quotidiennes et aux objets familiers, prêts dès que vous en avez besoin.',
    educatorsTitle: 'Éducateurs',
    educatorsBody: 'Un outil souple qui soutient les objectifs de communication en classe ou en séance de thérapie, au rythme qui convient le mieux.'
  },
  features: {
    title: 'Ce que vous pouvez faire',
    boardsTitle: 'Créer des tableaux pour chaque moment',
    boardsBody: 'Créez un tableau pour la maison, l\'école, les émotions ou n\'importe quelle routine. Les tableaux restent bien organisés à mesure que les besoins de votre enfant évoluent.',
    picturesTitle: 'Ajouter des images',
    picturesBody: 'Importez des photos de votre galerie, cherchez dans une bibliothèque de pictogrammes ou prenez une nouvelle photo directement dans l\'application.',
    speaksTitle: 'Parle à sa place',
    speaksBody: 'Votre enfant dépose des cartes images pour former une phrase et l\'application la dit à voix haute — une voix claire et immédiate.',
    organisedTitle: 'Reste organisé à mesure qu\'il grandit',
    organisedBody: 'Renommez, réordonnez et réorganisez les tableaux à tout moment — recherche, filtres et catégories gardent tout clair.'
  },
  how: {
    title: 'Comment ça marche',
    step1Title: 'Créer un tableau',
    step1Body: 'Donnez-lui le nom que vous voulez — Routine du matin, École, Émotions. Cela prend une dizaine de secondes.',
    step2Title: 'Ajouter des images',
    step2Body: 'Importez des photos de votre galerie, cherchez dans une bibliothèque de pictogrammes ou prenez une nouvelle photo directement dans l\'application.',
    step3Title: 'Lier l\'appareil',
    step3Body: 'Scannez un QR code depuis le panneau avec la tablette ou le téléphone de votre enfant. Ses tableaux y apparaissent.',
    step4Title: 'Le laisser parler',
    step4Body: 'Votre enfant dépose les images dans la bande-phrase et la lit : l\'application dit la phrase à voix haute. Simple, immédiat, et à lui.',
    deviceLink: 'Ouvrir sur l\'appareil de votre enfant'
  },
  oss: {
    title: 'Open source, pour toujours',
    lead: 'mavoix est open source. Nous croyons que chaque enfant mérite une voix — sans abonnement, sans barrières, sans coûts cachés.',
    contribute: 'Envie d\'aider ? Code, traductions et retours sont toujours les bienvenus.',
    github: 'Voir sur GitHub',
    app: 'application mavoix'
  }
}

const es = {
  langLabel: 'Idioma',
  nav: { who: 'Para quién', features: 'Funciones', how: 'Cómo funciona', start: 'Empezar' },
  pill: 'Código abierto · Compartir datos con Connect (opcional)',
  title: 'La voz de tu hijo, en imágenes',
  subtitle: 'Tableros de imágenes que permiten a tu hijo señalar, tocar y hacerse oír — directamente en tu navegador.',
  heroRoles: 'Los padres preparan los tableros en el panel. Los niños los usan en la aplicación.',
  forParents: 'Para padres',
  openPanel: 'Abrir el panel',
  forChildren: 'Para niños',
  openApp: 'Abrir la aplicación',
  seeHow: 'Ver cómo funciona ↓',
  heroAlt: 'Un tablero de mavoix con tarjetas de imágenes',
  aboutPre: 'mavoix es una aplicación de código abierto que permite comunicarse con ',
  aboutLink: 'imágenes (PECS)',
  aboutPost: ' a las personas no verbales. Crea tableros para casa, la escuela o la terapia — y deja que los toques hablen en voz alta.',
  sharingTitle: 'Compartir es cuidar',
  sharingPre: 'mavoix puede compartir datos de uso con',
  sharingPost: '— una plataforma de datos abiertos para aplicaciones de apoyo. Compartir es tu decisión: actívalo o desactívalo en cualquier momento en Ajustes globales. Se comparten los tiempos de uso y las frases construidas.',
  dataTitle: 'Estadísticas para comprender',
  dataBody: 'La página de estadísticas de uso muestra cómo se usa la aplicación: sesiones, días de uso y frases construidas, solo de tu cuenta. Si decides compartir con Connect, investigadores y desarrolladores también podrán ver cómo se usa en la práctica la comunicación con imágenes, para que las herramientas de apoyo evolucionen con evidencia y no con suposiciones.',
  who: {
    title: 'Para quién',
    parentsTitle: 'Padres y madres',
    parentsBody: 'Da a tu hijo una forma de decirte lo que quiere, cómo se siente y lo que necesita — incluso antes de que lleguen las palabras.',
    caregiversTitle: 'Cuidadores',
    caregiversBody: 'Prepara tableros adaptados a las rutinas diarias y a las cosas familiares, listos cuando los necesites.',
    educatorsTitle: 'Educadores',
    educatorsBody: 'Una herramienta flexible que apoya los objetivos de comunicación en el aula o en la sala de terapia, al ritmo que mejor funcione.'
  },
  features: {
    title: 'Qué puedes hacer',
    boardsTitle: 'Crea tableros para cada momento',
    boardsBody: 'Crea un tablero para casa, la escuela, las emociones o cualquier rutina. Los tableros se mantienen ordenados a medida que crecen las necesidades de tu hijo.',
    picturesTitle: 'Añade imágenes',
    picturesBody: 'Sube fotos de tu galería, busca en una biblioteca de símbolos o haz una foto nueva directamente en la aplicación.',
    speaksTitle: 'Habla por ellos',
    speaksBody: 'Tu hijo coloca tarjetas de imágenes para formar una frase y la aplicación la dice en voz alta — una voz clara e inmediata.',
    organisedTitle: 'Se mantiene ordenado mientras crecen',
    organisedBody: 'Renombra, reordena y reorganiza los tableros en cualquier momento — búsqueda, filtros y categorías lo mantienen todo claro.'
  },
  how: {
    title: 'Cómo funciona',
    step1Title: 'Crea un tablero',
    step1Body: 'Ponle el nombre que quieras — Rutina de la mañana, Escuela, Emociones. Lleva unos diez segundos.',
    step2Title: 'Añade imágenes',
    step2Body: 'Sube fotos de tu galería, busca en una biblioteca de símbolos o haz una foto nueva directamente en la aplicación.',
    step3Title: 'Vincula el dispositivo',
    step3Body: 'Escanea un código QR del panel con la tableta o el teléfono de tu hijo. Sus tableros aparecen allí.',
    step4Title: 'Déjale hablar',
    step4Body: 'Tu hijo coloca las imágenes en la tira de frase y la reproduce: la aplicación dice la frase en voz alta. Simple, inmediato y suyo.',
    deviceLink: 'Abrir en el dispositivo de tu hijo'
  },
  oss: {
    title: 'Código abierto, siempre',
    lead: 'mavoix es de código abierto. Creemos que todos los niños merecen una voz — sin suscripción, sin barreras, sin costes ocultos.',
    contribute: '¿Quieres ayudar? Código, traducciones y comentarios son siempre bienvenidos.',
    github: 'Ver en GitHub',
    app: 'aplicación mavoix'
  }
}

const pt = {
  langLabel: 'Idioma',
  nav: { who: 'Para quem', features: 'Funcionalidades', how: 'Como funciona', start: 'Começar' },
  pill: 'Código aberto · Partilha de dados opcional com o Connect',
  title: 'A voz do seu filho, em imagens',
  subtitle: 'Quadros de imagens que permitem ao seu filho apontar, tocar e ser ouvido — diretamente no seu navegador.',
  heroRoles: 'Os pais preparam os quadros no painel. As crianças usam-nos na aplicação.',
  forParents: 'Para pais',
  openPanel: 'Abrir o painel',
  forChildren: 'Para crianças',
  openApp: 'Abrir a aplicação',
  seeHow: 'Ver como funciona ↓',
  heroAlt: 'Um quadro mavoix com cartões de imagens',
  aboutPre: 'O mavoix é uma aplicação de código aberto que permite a pessoas não verbais comunicar através de ',
  aboutLink: 'imagens (PECS)',
  aboutPost: '. Crie quadros para casa, escola ou terapia — e deixe os toques falarem em voz alta.',
  sharingTitle: 'Partilhar é cuidar',
  sharingPre: 'O mavoix pode partilhar dados de utilização com o',
  sharingPost: '— uma plataforma de dados abertos para aplicações de apoio. A partilha é uma escolha sua: ative-a ou desative-a em qualquer momento em Definições globais. São partilhados os tempos de utilização e as frases construídas.',
  dataTitle: 'Estatísticas para compreender',
  dataBody: 'A página de estatísticas de utilização mostra como a aplicação é usada: sessões, dias de utilização e frases construídas, apenas da sua conta. Se escolher partilhar com o Connect, investigadores e criadores também podem ver como a comunicação por imagens é usada na prática, para que as ferramentas de apoio evoluam com base em evidências e não em suposições.',
  who: {
    title: 'Para quem',
    parentsTitle: 'Pais',
    parentsBody: 'Dê ao seu filho uma forma de dizer o que quer, como se sente e do que precisa — mesmo antes de as palavras chegarem.',
    caregiversTitle: 'Cuidadores',
    caregiversBody: 'Prepare quadros adaptados às rotinas diárias e às coisas familiares, prontos sempre que precisar.',
    educatorsTitle: 'Educadores',
    educatorsBody: 'Uma ferramenta flexível que apoia os objetivos de comunicação na sala de aula ou na sala de terapia, ao ritmo que funcionar melhor.'
  },
  features: {
    title: 'O que pode fazer',
    boardsTitle: 'Crie quadros para cada momento',
    boardsBody: 'Crie um quadro para casa, escola, emoções ou qualquer rotina. Os quadros mantêm-se organizados à medida que as necessidades do seu filho crescem.',
    picturesTitle: 'Adicione imagens',
    picturesBody: 'Carregue fotografias da sua galeria, pesquise numa biblioteca de símbolos ou tire uma nova fotografia diretamente na aplicação.',
    speaksTitle: 'Fala por eles',
    speaksBody: 'O seu filho coloca cartões de imagens para formar uma frase e a aplicação di-la em voz alta — uma voz clara e imediata.',
    organisedTitle: 'Mantém-se organizado à medida que crescem',
    organisedBody: 'Mude o nome, a ordem e a organização dos quadros a qualquer momento — pesquisa, filtros e categorias mantêm tudo claro.'
  },
  how: {
    title: 'Como funciona',
    step1Title: 'Crie um quadro',
    step1Body: 'Dê-lhe o nome que quiser — Rotina da manhã, Escola, Emoções. Demora cerca de dez segundos.',
    step2Title: 'Adicione imagens',
    step2Body: 'Carregue fotografias da sua galeria, pesquise numa biblioteca de símbolos ou tire uma nova fotografia diretamente na aplicação.',
    step3Title: 'Ligue o dispositivo',
    step3Body: 'Digitalize um código QR do painel com o tablet ou telemóvel do seu filho. Os quadros aparecem lá.',
    step4Title: 'Deixe-o falar',
    step4Body: 'O seu filho coloca as imagens na faixa de frase e reproduz: a aplicação diz a frase em voz alta. Simples, imediato e dele.',
    deviceLink: 'Abrir no dispositivo do seu filho'
  },
  oss: {
    title: 'Código aberto, sempre',
    lead: 'O mavoix é de código aberto. Acreditamos que todas as crianças merecem uma voz — sem subscrição, sem barreiras, sem custos escondidos.',
    contribute: 'Quer ajudar? Código, traduções e comentários são sempre bem-vindos.',
    github: 'Ver no GitHub',
    app: 'aplicação mavoix'
  }
}

const de = {
  langLabel: 'Sprache',
  nav: { who: 'Für wen', features: 'Funktionen', how: 'So funktioniert\'s', start: 'Loslegen' },
  pill: 'Open Source · Optionaler Datenaustausch mit Connect',
  title: 'Die Stimme Ihres Kindes, in Bildern',
  subtitle: 'Bildtafeln, mit denen Ihr Kind zeigen, tippen und sich Gehör verschaffen kann — direkt in Ihrem Browser.',
  heroRoles: 'Eltern richten die Tafeln im Panel ein. Kinder nutzen sie in der App.',
  forParents: 'Für Eltern',
  openPanel: 'Panel öffnen',
  forChildren: 'Für Kinder',
  openApp: 'App öffnen',
  seeHow: 'So funktioniert\'s ansehen ↓',
  heroAlt: 'Eine mavoix-Tafel mit Bildkarten',
  aboutPre: 'mavoix ist eine Open-Source-Anwendung, die nichtsprechenden Menschen ermöglicht, mit ',
  aboutLink: 'Bildern (PECS)',
  aboutPost: ' zu kommunizieren. Erstellen Sie Tafeln für zu Hause, die Schule oder die Therapie — und lassen Sie Fingertipps laut sprechen.',
  sharingTitle: 'Teilen ist Fürsorge',
  sharingPre: 'mavoix kann Nutzungsdaten teilen mit',
  sharingPost: '— einer offenen Datenplattform für Assistenzanwendungen. Das Teilen ist Ihre Entscheidung: Sie können es jederzeit in den Globalen Einstellungen ein- oder ausschalten. Geteilt werden Nutzungszeiten und gebildete Sätze.',
  dataTitle: 'Statistiken und Erkenntnisse',
  dataBody: 'Die Nutzungsstatistik zeigt, wie die App genutzt wird: Sitzungen, Nutzungstage und gebildete Sätze, nur für Ihr Konto. Wenn Sie sich für das Teilen mit Connect entscheiden, können auch Forschende und Entwickler sehen, wie bildbasierte Kommunikation in der Praxis genutzt wird — damit Assistenzwerkzeuge auf Belegen statt auf Vermutungen aufbauen.',
  who: {
    title: 'Für wen',
    parentsTitle: 'Eltern',
    parentsBody: 'Geben Sie Ihrem Kind eine Möglichkeit, Ihnen zu sagen, was es möchte, wie es sich fühlt und was es braucht — noch bevor die Worte kommen.',
    caregiversTitle: 'Betreuungspersonen',
    caregiversBody: 'Richten Sie Tafeln für den Alltag und vertraute Dinge ein, bereit, wann immer Sie sie brauchen.',
    educatorsTitle: 'Pädagoginnen und Pädagogen',
    educatorsBody: 'Ein flexibles Werkzeug, das Kommunikationsziele im Klassenzimmer oder im Therapieraum unterstützt — in dem Tempo, das am besten passt.'
  },
  features: {
    title: 'Was Sie tun können',
    boardsTitle: 'Tafeln für jeden Moment erstellen',
    boardsBody: 'Erstellen Sie eine Tafel für zu Hause, die Schule, Gefühle oder jede Routine. Die Tafeln bleiben übersichtlich, während die Bedürfnisse Ihres Kindes wachsen.',
    picturesTitle: 'Bilder hinzufügen',
    picturesBody: 'Laden Sie Fotos aus Ihrer Galerie hoch, durchsuchen Sie eine Symbolbibliothek oder machen Sie direkt in der App ein neues Foto.',
    speaksTitle: 'Spricht für sie',
    speaksBody: 'Ihr Kind legt Bildkarten zu einem Satz zusammen und die App spricht ihn laut aus — eine sofortige, klare Stimme.',
    organisedTitle: 'Bleibt übersichtlich, während sie wachsen',
    organisedBody: 'Benennen Sie Tafeln jederzeit um, sortieren und ordnen Sie sie neu — Suche, Filter und Kategorien sorgen für Klarheit.'
  },
  how: {
    title: 'So funktioniert\'s',
    step1Title: 'Tafel erstellen',
    step1Body: 'Nennen Sie sie, wie Sie möchten — Morgenroutine, Schule, Gefühle. Dauert etwa zehn Sekunden.',
    step2Title: 'Bilder hinzufügen',
    step2Body: 'Laden Sie Fotos aus Ihrer Galerie hoch, durchsuchen Sie eine Symbolbibliothek oder machen Sie direkt in der App ein neues Foto.',
    step3Title: 'Gerät verknüpfen',
    step3Body: 'Scannen Sie einen QR-Code aus dem Panel mit dem Tablet oder Handy Ihres Kindes. Die Tafeln erscheinen dort.',
    step4Title: 'Sprechen lassen',
    step4Body: 'Ihr Kind legt die Bilder in die Satzleiste und spielt sie ab: Die App spricht den Satz laut aus. Einfach, unmittelbar und seine eigene Stimme.',
    deviceLink: 'Auf dem Gerät Ihres Kindes öffnen'
  },
  oss: {
    title: 'Open Source, für immer',
    lead: 'mavoix ist Open Source. Wir glauben, dass jedes Kind eine Stimme verdient — kein Abo, keine Hürden, keine versteckten Kosten.',
    contribute: 'Möchten Sie helfen? Code, Übersetzungen und Feedback sind jederzeit willkommen.',
    github: 'Auf GitHub ansehen',
    app: 'mavoix-App'
  }
}

const it = {
  langLabel: 'Lingua',
  nav: { who: 'Per chi è', features: 'Funzionalità', how: 'Come funziona', start: 'Inizia' },
  pill: 'Open source · Condivisione dati facoltativa con Connect',
  title: 'La voce di tuo figlio, in immagini',
  subtitle: 'Tabelle di immagini che permettono a tuo figlio di indicare, toccare e farsi sentire — direttamente nel tuo browser.',
  heroRoles: 'I genitori preparano le tabelle nel pannello. I bambini le usano nell\'app.',
  forParents: 'Per i genitori',
  openPanel: 'Apri il pannello',
  forChildren: 'Per i bambini',
  openApp: 'Apri l\'app',
  seeHow: 'Scopri come funziona ↓',
  heroAlt: 'Una tabella mavoix con carte illustrate',
  aboutPre: 'mavoix è un\'applicazione open source che permette alle persone non verbali di comunicare con le ',
  aboutLink: 'immagini (PECS)',
  aboutPost: '. Crea tabelle per casa, scuola o terapia — e lascia che i tocchi parlino ad alta voce.',
  sharingTitle: 'Condividere è prendersi cura',
  sharingPre: 'mavoix può condividere dati di utilizzo con',
  sharingPost: '— una piattaforma di dati aperti per applicazioni di supporto. La condivisione è una tua scelta: attivala o disattivala in qualsiasi momento nelle Impostazioni globali. Si condividono i tempi di utilizzo e le frasi costruite.',
  dataTitle: 'Statistiche per capire',
  dataBody: 'La pagina delle statistiche di utilizzo mostra come viene usata l\'app: sessioni, giorni di utilizzo e frasi costruite, solo per il tuo account. Se scegli di condividere con Connect, anche ricercatori e sviluppatori possono vedere come viene usata in pratica la comunicazione per immagini, così gli strumenti di supporto crescono sulla base di prove e non di supposizioni.',
  who: {
    title: 'Per chi è',
    parentsTitle: 'Genitori',
    parentsBody: 'Dai a tuo figlio un modo per dirti cosa vuole, come si sente e di cosa ha bisogno — anche prima che arrivino le parole.',
    caregiversTitle: 'Caregiver',
    caregiversBody: 'Prepara tabelle su misura per le routine quotidiane e le cose familiari, pronte quando ti servono.',
    educatorsTitle: 'Educatori',
    educatorsBody: 'Uno strumento flessibile che sostiene gli obiettivi di comunicazione in classe o in terapia, con il ritmo più adatto.'
  },
  features: {
    title: 'Cosa puoi fare',
    boardsTitle: 'Crea tabelle per ogni momento',
    boardsBody: 'Crea una tabella per casa, scuola, emozioni o qualsiasi routine. Le tabelle restano ordinate man mano che crescono le esigenze di tuo figlio.',
    picturesTitle: 'Aggiungi immagini',
    picturesBody: 'Carica foto dalla tua galleria, cerca in una libreria di simboli o scatta una nuova foto direttamente nell\'app.',
    speaksTitle: 'Parla al posto loro',
    speaksBody: 'Tuo figlio trascina le carte illustrate per formare una frase e l\'app la dice ad alta voce — una voce chiara e immediata.',
    organisedTitle: 'Resta ordinato mentre crescono',
    organisedBody: 'Rinomina, riordina e riorganizza le tabelle in qualsiasi momento — ricerca, filtri e categorie mantengono tutto chiaro.'
  },
  how: {
    title: 'Come funziona',
    step1Title: 'Crea una tabella',
    step1Body: 'Dalle il nome che vuoi — Routine del mattino, Scuola, Emozioni. Servono circa dieci secondi.',
    step2Title: 'Aggiungi immagini',
    step2Body: 'Carica foto dalla tua galleria, cerca in una libreria di simboli o scatta una nuova foto direttamente nell\'app.',
    step3Title: 'Collega il dispositivo',
    step3Body: 'Inquadra un codice QR dal pannello con il tablet o lo smartphone di tuo figlio. Le tabelle appaiono lì.',
    step4Title: 'Lascialo parlare',
    step4Body: 'Tuo figlio trascina le immagini nella barra della frase e la riproduce: l\'app dice la frase ad alta voce. Semplice, immediato e suo.',
    deviceLink: 'Apri sul dispositivo di tuo figlio'
  },
  oss: {
    title: 'Open source, sempre',
    lead: 'mavoix è open source. Crediamo che ogni bambino meriti una voce — nessun abbonamento, nessuna barriera, nessun costo nascosto.',
    contribute: 'Vuoi aiutare? Codice, traduzioni e feedback sono sempre i benvenuti.',
    github: 'Vedi su GitHub',
    app: 'app mavoix'
  }
}

const nl = {
  langLabel: 'Taal',
  nav: { who: 'Voor wie', features: 'Functies', how: 'Hoe het werkt', start: 'Aan de slag' },
  pill: 'Open source · Optioneel gegevens delen met Connect',
  title: 'De stem van je kind, in beelden',
  subtitle: 'Beeldborden waarmee je kind kan wijzen, tikken en gehoord wordt — direct in je browser.',
  heroRoles: 'Ouders stellen de borden in het paneel in. Kinderen gebruiken ze in de app.',
  forParents: 'Voor ouders',
  openPanel: 'Open het paneel',
  forChildren: 'Voor kinderen',
  openApp: 'Open de app',
  seeHow: 'Bekijk hoe het werkt ↓',
  heroAlt: 'Een mavoix-bord met beeldkaarten',
  aboutPre: 'mavoix is een open source-applicatie waarmee niet-sprekende mensen met ',
  aboutLink: 'beelden (PECS)',
  aboutPost: ' kunnen communiceren. Maak borden voor thuis, school of therapie — en laat tikken hardop spreken.',
  sharingTitle: 'Delen is zorgen',
  sharingPre: 'mavoix kan gebruiksgegevens delen met',
  sharingPost: '— een open dataplatform voor hulpmiddelen-apps. Delen is jouw keuze: zet het op elk moment aan of uit in Algemene instellingen. Gedeeld worden gebruikstijden en de gemaakte zinnen.',
  dataTitle: 'Statistieken voor inzicht',
  dataBody: 'De pagina met gebruiksstatistieken laat zien hoe de app wordt gebruikt: sessies, gebruiksdagen en gemaakte zinnen, alleen voor jouw account. Kies je ervoor te delen met Connect, dan kunnen ook onderzoekers en makers zien hoe communicatie met beelden in de praktijk wordt gebruikt, zodat hulpmiddelen groeien op basis van bewijs in plaats van giswerk.',
  who: {
    title: 'Voor wie',
    parentsTitle: 'Ouders',
    parentsBody: 'Geef je kind een manier om te vertellen wat het wil, hoe het zich voelt en wat het nodig heeft — nog voordat de woorden komen.',
    caregiversTitle: 'Zorgverleners',
    caregiversBody: 'Maak borden op maat van dagelijkse routines en vertrouwde dingen, klaar wanneer je ze nodig hebt.',
    educatorsTitle: 'Onderwijzers',
    educatorsBody: 'Een flexibel hulpmiddel dat communicatiedoelen ondersteunt in de klas of therapieruimte, in het tempo dat het best past.'
  },
  features: {
    title: 'Wat je kunt doen',
    boardsTitle: 'Maak borden voor elk moment',
    boardsBody: 'Maak een bord voor thuis, school, gevoelens of elke routine. Borden blijven overzichtelijk naarmate de behoeften van je kind groeien.',
    picturesTitle: 'Voeg afbeeldingen toe',
    picturesBody: 'Upload foto\'s uit je galerij, zoek in een symbolenbibliotheek of maak direct in de app een nieuwe foto.',
    speaksTitle: 'Spreekt voor hen',
    speaksBody: 'Je kind legt beeldkaarten neer tot een zin en de app zegt die hardop — een directe, duidelijke stem.',
    organisedTitle: 'Blijft overzichtelijk terwijl ze groeien',
    organisedBody: 'Hernoem, herorden en herorganiseer borden op elk moment — zoeken, filters en categorieën houden alles helder.'
  },
  how: {
    title: 'Hoe het werkt',
    step1Title: 'Maak een bord',
    step1Body: 'Geef het elke naam — Ochtendroutine, School, Gevoelens. Kost ongeveer tien seconden.',
    step2Title: 'Voeg afbeeldingen toe',
    step2Body: 'Upload foto\'s uit je galerij, zoek in een symbolenbibliotheek of maak direct in de app een nieuwe foto.',
    step3Title: 'Koppel het apparaat',
    step3Body: 'Scan een QR-code uit het paneel met de tablet of telefoon van je kind. De borden verschijnen daar.',
    step4Title: 'Laat ze spreken',
    step4Body: 'Je kind legt de afbeeldingen in de zinsbalk en speelt die af: de app zegt de zin hardop. Eenvoudig, direct en van henzelf.',
    deviceLink: 'Openen op het apparaat van je kind'
  },
  oss: {
    title: 'Open source, altijd',
    lead: 'mavoix is open source. Wij geloven dat elk kind een stem verdient — geen abonnement, geen drempels, geen verborgen kosten.',
    contribute: 'Wil je helpen? Code, vertalingen en feedback zijn altijd welkom.',
    github: 'Bekijk op GitHub',
    app: 'mavoix-app'
  }
}

export default {
  'en-us': en,
  'fr-fr': fr,
  'es-es': es,
  'pt-pt': pt,
  'de-de': de,
  'it-it': it,
  'nl-nl': nl
}

/** Languages offered by the landing page selector: [locale, short label, native name]. */
export const LANDING_LANGUAGES = [
  ['en-us', 'EN', 'English'],
  ['fr-fr', 'FR', 'Français'],
  ['es-es', 'ES', 'Español'],
  ['pt-pt', 'PT', 'Português'],
  ['de-de', 'DE', 'Deutsch'],
  ['it-it', 'IT', 'Italiano'],
  ['nl-nl', 'NL', 'Nederlands']
]
